import React, { useState } from 'react';

import { Button, createStyles, Group, Stack, Text, TextInput, useMantineTheme } from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import MovementSourceSelect from 'components/form-fields/auto-complete-fields/MovementSourceSelect';
import { useAuth } from 'context/auth-context';

import BenchmarksTableContainer from './components/BenchmarksTableContainer';
import ModalBenchmarkForm from './components/ModalBenchmarkForm';

const useStyles = createStyles(theme => ({
  container: {
    padding: theme.spacing.xl,
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    overflow: 'hidden',
  },
}));

const BenchmarkLibrary = (): JSX.Element => {
  const theme = useMantineTheme();
  const { classes } = useStyles();
  const { hasPerm } = useAuth();
  const canMutate = hasPerm('WorkoutMutate');

  const [search, setSearch] = useState<string>('');
  const [sourceFilter, setSourceFilter] = useState<string | null>(null);
  const [debouncedSearch] = useDebouncedValue<string>(search, 300);

  const [createFormOpened, { open: openCreateForm, close: closeCreateForm }] = useDisclosure(false);

  return (
    <div className={classes.container}>
      <Stack spacing='md' sx={{ flexGrow: 1, overflow: 'hidden' }}>
        <Group position='apart' align='flex-end'>
          <Group spacing='md'>
            <TextInput
              placeholder='Search benchmarks...'
              icon={<IconSearch size={16} />}
              value={search}
              onChange={e => setSearch(e.currentTarget.value)}
              style={{ minWidth: 300 }}
            />
            <MovementSourceSelect
              value={sourceFilter}
              onChange={setSourceFilter}
              style={{ minWidth: 200 }}
            />
          </Group>

          <Button
            leftIcon={<IconPlus size={theme.fontSizes.md} />}
            onClick={openCreateForm}
            disabled={!canMutate}
            data-cy='createBenchmarkButton'
          >
            New Benchmark
          </Button>
        </Group>

        <Text color='dimmed' size='sm'>
          Standardized workout or fitness test that combines one or more movements and can be repeated over
          time to measure progress and performance. Popular examples: Fran (thrusters + pull-ups), Murph (run
          + bodyweight), and Cindy (AMRAP rounds).
        </Text>

        <BenchmarksTableContainer
          permission={canMutate}
          search={debouncedSearch}
          source={sourceFilter ?? undefined}
        />

        <ModalBenchmarkForm opened={createFormOpened} closeHandler={closeCreateForm} type='create' />
      </Stack>
    </div>
  );
};

export default BenchmarkLibrary;
